<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  VERIFICATION_STATUSES,
  PRIORITIES,
} from 'dashboard/components-next/Kanban/composables/useKanbanBoard.js';

const props = defineProps({
  open: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const company = defineModel('company', { type: String, default: '' });
const priority = defineModel('priority', { type: String, default: '' });
const channel = defineModel('channel', { type: String, default: '' });
const verificationStatus = defineModel('verificationStatus', {
  type: String,
  default: '',
});
const sortBy = defineModel('sortBy', { type: String, required: true });

const { t } = useI18n();

const isOpen = computed(() => props.open);

const verificationOptions = computed(() => [
  { label: t('KANBAN.FILTERS.ALL'), value: '' },
  ...VERIFICATION_STATUSES.map(status => ({ label: status, value: status })),
]);

const PRIORITY_LABEL_KEYS = {
  urgent: 'CONVERSATION.PRIORITY.OPTIONS.URGENT',
  high: 'CONVERSATION.PRIORITY.OPTIONS.HIGH',
  medium: 'CONVERSATION.PRIORITY.OPTIONS.MEDIUM',
  low: 'CONVERSATION.PRIORITY.OPTIONS.LOW',
};
const priorityOptions = computed(() => [
  { label: t('KANBAN.FILTERS.ALL'), value: '' },
  ...PRIORITIES.map(p => ({ label: t(PRIORITY_LABEL_KEYS[p]), value: p })),
]);

const CHANNEL_OPTIONS = [
  { label: 'Website', value: 'Channel::WebWidget' },
  { label: 'WhatsApp', value: 'Channel::Whatsapp' },
  { label: 'Facebook', value: 'Channel::FacebookPage' },
  { label: 'Instagram', value: 'Channel::Instagram' },
  { label: 'Email', value: 'Channel::Email' },
  { label: 'API', value: 'Channel::Api' },
  { label: 'Telegram', value: 'Channel::Telegram' },
  { label: 'Line', value: 'Channel::Line' },
  { label: 'SMS', value: 'Channel::Sms' },
  { label: 'Voice', value: 'Channel::Voip' },
];
const channelOptions = computed(() => [
  { label: t('KANBAN.FILTERS.ALL'), value: '' },
  ...CHANNEL_OPTIONS,
]);

const sortOptions = computed(() => [
  { label: t('KANBAN.SORT.NEWEST'), value: 'newest' },
  { label: t('KANBAN.SORT.OLDEST'), value: 'oldest' },
  { label: t('KANBAN.SORT.PRIORITY'), value: 'priority' },
  { label: t('KANBAN.SORT.NEXT_FOLLOW_UP'), value: 'next_follow_up' },
  { label: t('KANBAN.SORT.LAST_CONVERSATION'), value: 'last_conversation' },
]);

const closeDrawer = () => emit('close');
</script>

<template>
  <TeleportWithDirection to="body">
    <Transition name="kanban-drawer-fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 bg-black/30"
        role="presentation"
        @click.self="closeDrawer"
      >
        <aside
          class="fixed inset-y-0 flex flex-col w-full max-w-xs bg-n-solid-1 shadow-xl outline outline-1 outline-n-container ltr:end-0 rtl:start-0"
          role="dialog"
          aria-modal="true"
          :aria-label="t('KANBAN.ADVANCED_FILTERS.TITLE')"
        >
          <header
            class="flex items-center justify-between gap-4 px-5 py-4 border-b border-n-weak"
          >
            <h2 class="text-base font-medium text-n-slate-12">
              {{ t('KANBAN.ADVANCED_FILTERS.TITLE') }}
            </h2>
            <Button
              ghost
              slate
              size="sm"
              icon="i-lucide-x"
              @click="closeDrawer"
            />
          </header>

          <div class="flex flex-col flex-1 gap-4 px-5 py-4 overflow-y-auto">
            <p class="text-xs text-n-slate-10">
              {{ t('KANBAN.ADVANCED_FILTERS.APPLY_HINT') }}
            </p>

            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.FILTERS.COMPANY') }}
              </span>
              <Input
                v-model="company"
                :placeholder="t('KANBAN.FILTERS.COMPANY')"
                custom-input-class="h-8 !pt-1 !pb-1"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.FILTERS.PRIORITY') }}
              </span>
              <ComboBox
                v-model="priority"
                :options="priorityOptions"
                :placeholder="t('KANBAN.FILTERS.PRIORITY')"
                class="[&>div>button]:h-8"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.FILTERS.CHANNEL') }}
              </span>
              <ComboBox
                v-model="channel"
                :options="channelOptions"
                :placeholder="t('KANBAN.FILTERS.CHANNEL')"
                class="[&>div>button]:h-8"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.FILTERS.VERIFICATION') }}
              </span>
              <ComboBox
                v-model="verificationStatus"
                :options="verificationOptions"
                :placeholder="t('KANBAN.FILTERS.VERIFICATION')"
                class="[&>div>button]:h-8"
              />
            </div>

            <div class="flex flex-col gap-1.5 pt-2 border-t border-n-weak">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.SORT.LABEL') }}
              </span>
              <ComboBox
                v-model="sortBy"
                :options="sortOptions"
                :placeholder="t('KANBAN.SORT.LABEL')"
                class="[&>div>button]:h-8"
              />
            </div>
          </div>
        </aside>
      </div>
    </Transition>
  </TeleportWithDirection>
</template>

<style scoped>
.kanban-drawer-fade-enter-active,
.kanban-drawer-fade-leave-active {
  transition: opacity 0.2s ease;
}
.kanban-drawer-fade-enter-from,
.kanban-drawer-fade-leave-to {
  opacity: 0;
}
</style>
